/**
 * Os contratos das telas novas do painel (spec 096).
 *
 * Zod aqui não é decoração: a resposta chega do servidor e é PARSEADA antes de
 * virar estado. Campo que muda de nome ou de tipo quebra na borda, com a
 * mensagem dizendo qual é, em vez de virar `undefined` renderizado como vazio
 * três componentes adiante.
 */
import { z } from 'zod'

/* -------------------------------------------------------------------------- */
/* Execuções                                                                   */
/* -------------------------------------------------------------------------- */

export const organizationExecutionSchema = z.object({
  id: z.string().uuid(),
  agent_id: z.string().uuid(),
  agent_name: z.string(),
  project_id: z.string().uuid(),
  project_name: z.string(),
  status: z.string(),
  cost: z.number().nullable(),
  total_tokens: z.number(),
  execution_time_ms: z.number().nullable(),
  error_message: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  /* O que o carimbo da spec 117 tornou possível. Nulos quando a execução não
     veio do atendimento — e a ausência é significado, não falta. */
  channel_session_id: z.string().uuid().nullable().default(null),
  etapa_alcancada: z.string().nullable().default(null),
  motivo: z.string().nullable().default(null),
  fonte_do_motivo: z.string().nullable().default(null),
  canal: z.string().nullable().default(null),
  /** Quem foi identificado naquela conversa, do cadastro do escritório. */
  cliente: z.string().nullable().default(null),
  /** Quantos turnos aquela CONVERSA teve — não esta execução. */
  turnos_da_conversa: z.number().nullable().default(null),
  /** O texto já foi expurgado pela retenção. A linha PERMANECE na contagem. */
  texto_expirado: z.boolean().default(false),
})

export type OrganizationExecution = z.infer<typeof organizationExecutionSchema>

export const organizationExecutionListSchema = z.object({
  items: z.array(organizationExecutionSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  /* AS DUAS CONTAGENS, SEMPRE JUNTAS. O funil conta CONVERSA e a execução
     conta TURNO: a mesma queda é 33 num lugar e 140 no outro. Mostrar uma só,
     sem dizer qual, faz quem lê concluir que o relatório está errado. */
  total_de_conversas: z.number().nullable().default(null),
  total_de_turnos: z.number().nullable().default(null),
})

/* -------------------------------------------------------------------------- */
/* Execuções agrupadas por atendimento (spec 128)                              */
/*                                                                            */
/* Espelho do contrato `GET /organizations/{org_id}/executions/attendances`.   */
/* Um atendimento é a sessão do canal e o ciclo: do primeiro contato (ou da    */
/* volta) até o encerramento seguinte.                                         */
/* -------------------------------------------------------------------------- */

export const groupExecutionSchema = z.object({
  id: z.string().uuid(),
  agent_name: z.string(),
  status: z.string(),
  cost: z.number().nullable(),
  total_tokens: z.number(),
  execution_time_ms: z.number().nullable(),
  error_message: z.string().nullable(),
  created_at: z.string(),
  text_expired: z.boolean().default(false),
})
export type GroupExecution = z.infer<typeof groupExecutionSchema>

export const askStatusSchema = z.enum(['fulfilled', 'not_fulfilled', 'unanswered'])
export type AskStatus = z.infer<typeof askStatusSchema>

export const attendanceSummaryViewSchema = z.object({
  /** Nulo no item NÃO GRAVADO de conversa anterior ao resumo (`before_summary`). */
  id: z.string().uuid().nullable(),
  kind: z.enum(['handoff', 'closed']),
  occurred_at: z.string(),
  status: z.enum(['pending', 'ready', 'unavailable']),
  reason: z.string().nullable(),
  /** Sempre verdadeiro: existe para a tela marcar a região sem inferir. */
  generated_by_ai: z.boolean(),
  ask_statuses: z.array(askStatusSchema).default([]),
  suggested_category: z.string().nullable().default(null),
  suggested_priority: z.string().nullable().default(null),
  /** A prosa: nula quando não há resumo pronto, ou quando a retenção a apagou. */
  prose: z
    .object({
      subject: z.string().nullable().default(null),
      text: z.string().nullable().default(null),
      asks: z
        .array(
          z.object({
            text: z.string(),
            status: z.string(),
            note: z.string().nullable().default(null),
          })
        )
        .default([]),
      warnings: z.array(z.string()).default([]),
      todo: z.array(z.string()).default([]),
      gathered: z
        .array(z.object({ label: z.string(), value: z.string() }))
        .default([]),
    })
    .nullable(),
  prose_erased_at: z.string().nullable().default(null),
})
export type AttendanceSummaryView = z.infer<typeof attendanceSummaryViewSchema>

export const attendanceGroupSchema = z.object({
  key: z.string(),
  channel_session_id: z.string().uuid().nullable(),
  cycle: z.number().nullable(),
  state: z.enum(['in_progress', 'handoff', 'closed', 'orphan']),
  outcome: z.string().nullable(),
  contact: z.object({
    name: z.string().nullable(),
    phone: z.string().nullable(),
  }),
  client_name: z.string().nullable(),
  channel: z.string().nullable(),
  started_at: z.string(),
  ended_at: z.string().nullable(),
  duration_ms: z.number().nullable(),
  cost: z.number().nullable(),
  total_tokens: z.number(),
  executions_total: z.number(),
  executions: z.array(groupExecutionSchema),
  not_fulfilled: z.boolean(),
  handoff: z
    .object({
      reason: z.string(),
      customer_message: z.string().nullable(),
      requested_at: z.string(),
      queued: z.boolean(),
    })
    .nullable(),
  deliveries: z.array(
    z.object({
      name: z.string().nullable(),
      delivered_at: z.string(),
      metadata: z.record(z.unknown()),
    })
  ),
  summaries: z.array(attendanceSummaryViewSchema),
})
export type AttendanceGroup = z.infer<typeof attendanceGroupSchema>

export const attendanceListResponseSchema = z.object({
  items: z.array(attendanceGroupSchema),
  total_groups: z.number(),
  total_turns: z.number(),
  total_conversations: z.number(),
  limit: z.number(),
  offset: z.number(),
})
export type AttendanceListResponse = z.infer<typeof attendanceListResponseSchema>

/* -------------------------------------------------------------------------- */
/* A execução aberta (spec 117)                                                */
/* -------------------------------------------------------------------------- */

export const caminhoNoFunilSchema = z.object({
  objetivo: z.string(),
  etapas_percorridas: z.array(z.string()),
  etapa_alcancada: z.string().nullable(),
  status: z.string(),
  motivo: z.string().nullable(),
  fonte_do_motivo: z.string().nullable(),
  convertido_em: z.string().nullable(),
  houve_transbordo: z.boolean(),
})

/** As condições em que o turno começou. Sobrevive ao expurgo do texto. */
export const sobOQueRodouSchema = z.object({
  blueprint_version: z.number().nullable(),
  system_prompt_sha256: z.string().nullable(),
  tools_offered: z.array(z.string()),
  llm_params: z.record(z.unknown()),
  code_revision: z.string().nullable(),
  model_requested: z.string().nullable(),
  model_resolved: z.string().nullable(),
})

/**
 * Um turno da conversa.
 *
 * A listagem tem uma linha por CONVERSA, e abrir essa linha mostrava um turno
 * só — numa conversa de quatro, o pedido do cliente, a busca que localizou o
 * documento e a entrega ficavam invisíveis.
 */
export const turnoDaConversaSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string(),
  mensagem_do_cliente: z.string().nullable(),
  resposta_do_assistente: z.string().nullable(),
  tool_calls: z.array(z.unknown()).default([]),
  cost: z.number().nullable().default(null),
  total_tokens: z.number().default(0),
  execution_time_ms: z.number().nullable().default(null),
  texto_expirado: z.boolean().default(false),
  /** Qual deles a URL abriu — sem a marca some o ponto de onde se veio. */
  e_o_aberto: z.boolean().default(false),
})
export type TurnoDaConversa = z.infer<typeof turnoDaConversaSchema>

/**
 * Como o cliente foi identificado na conversa.
 *
 * NÃO é etapa de funil: o objetivo declarado pelo escritório tem as etapas
 * dele, e um escritório pode nem exigir identificação. É um fato da conversa.
 *
 * O identificador do sujeito não vem — `session_events` guarda doze meses e o
 * conteúdo expira no prazo do escritório (ADR-0071).
 */
export const identificacaoSchema = z.object({
  nivel: z.string(),
  motivo: z.string().nullable().default(null),
  origem: z.string().nullable().default(null),
  quando: z.string(),
  /** Quem foi identificado, do cadastro do escritório. */
  cliente: z.string().nullable().default(null),
  /** O identificador como o cadastro o guarda: mascarado. */
  identificador_mascarado: z.string().nullable().default(null),
})
export type Identificacao = z.infer<typeof identificacaoSchema>

export const execucaoAbertaSchema = z.object({
  id: z.string().uuid(),
  agent_name: z.string(),
  status: z.string(),
  em_andamento: z.boolean(),
  provider: z.string(),
  model: z.string(),
  prompt_tokens: z.number(),
  completion_tokens: z.number(),
  total_tokens: z.number(),
  cost: z.number().nullable(),
  execution_time_ms: z.number().nullable(),
  tool_calls: z.array(z.unknown()),
  error_message: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  sob_o_que_rodou: sobOQueRodouSchema,
  /**
   * De qual conversa a execução é. NULO quando ela não veio do atendimento — e
   * é ESTA a condição que decide se as seções de conversa e de funil aparecem
   * (FR-020), não a presença do objetivo: execução do atendimento cujo
   * objetivo ainda não abriu tem conversa e não tem funil.
   */
  channel_session_id: z.string().uuid().nullable().default(null),
  caminho_no_funil: caminhoNoFunilSchema.nullable().default(null),
  mensagem_do_cliente: z.string().nullable().default(null),
  resposta_do_assistente: z.string().nullable().default(null),
  texto_expirado: z.boolean().default(false),
  link_no_chatwoot: z.string().nullable().default(null),
  /** A conversa inteira. Vazia quando a execução não veio do atendimento. */
  identificacao: identificacaoSchema.nullable().default(null),
  turnos: z.array(turnoDaConversaSchema).default([]),
  /** Quantos a conversa tem de verdade, contra quantos vieram. */
  total_de_turnos: z.number().default(0),
})

export type ExecucaoAberta = z.infer<typeof execucaoAbertaSchema>
export type CaminhoNoFunil = z.infer<typeof caminhoNoFunilSchema>

export type OrganizationExecutionList = z.infer<typeof organizationExecutionListSchema>

/**
 * O TETO DO TRABALHO, em minutos — o mesmo `TETO_S` de
 * `backend/app/services/agent_work.py`.
 *
 * Execução em curso sem sinal há mais disto passou do tempo que o servidor se
 * dá para terminar um trabalho. A tela a separa de "em curso" porque a diferença
 * importa para quem olha: uma está andando, a outra não vai terminar sozinha.
 *
 * **O NÚMERO ACOMPANHA O BACKEND.** Divergir daria à tela uma afirmação que o
 * servidor não cumpre — foi o que aconteceu enquanto ele era 30 e o backend
 * cortava noutro instante.
 */
export const TETO_DA_FILA_MIN = 25

export type EstadoDaExecucao =
  | 'em_curso'
  | 'parada'
  | 'concluida'
  | 'falhou'
  | 'cancelada'
  | 'na_fila'

const EM_ANDAMENTO = new Set(['running', 'processing', 'in_progress'])

/** Idade do último sinal, em minutos. */
export function minutosSemSinal(
  execucao: OrganizationExecution,
  agora: Date = new Date()
): number {
  const ultimo = execucao.updated_at ?? execucao.started_at ?? execucao.created_at
  return (agora.getTime() - new Date(ultimo).getTime()) / 60000
}

/** O estado que a TELA mostra — que não é o mesmo que o banco guarda. */
export function estadoDaExecucao(
  execucao: OrganizationExecution,
  agora: Date = new Date()
): EstadoDaExecucao {
  const bruto = execucao.status.toLowerCase()
  if (bruto === 'completed' || bruto === 'success') return 'concluida'
  if (bruto === 'failed' || bruto === 'error') return 'falhou'
  if (bruto === 'cancelled' || bruto === 'canceled') return 'cancelada'
  if (bruto === 'pending' || bruto === 'queued') return 'na_fila'
  if (EM_ANDAMENTO.has(bruto)) {
    return minutosSemSinal(execucao, agora) > TETO_DA_FILA_MIN ? 'parada' : 'em_curso'
  }
  return 'em_curso'
}

/* -------------------------------------------------------------------------- */
/* Custos                                                                      */
/* -------------------------------------------------------------------------- */

export const agentCostEntrySchema = z.object({
  agent_id: z.string().uuid(),
  agent_name: z.string(),
  total_cost: z.coerce.number(),
  total_executions: z.number(),
})

export const projectCostEntrySchema = z.object({
  project_id: z.string().uuid(),
  project_name: z.string(),
  total_cost: z.coerce.number(),
  total_executions: z.number(),
})

/**
 * `z.coerce.number()` nos valores de dinheiro, e não `z.number()`.
 *
 * O servidor serializa `Decimal` como STRING (`"10.00"`) — é o que preserva a
 * precisão no JSON. Declarar `number` faria o parse falhar em toda resposta com
 * custo, e a tela mostraria erro de contrato onde o contrato está certo.
 */
export const organizationCostSummarySchema = z.object({
  org_id: z.string().uuid(),
  total_cost: z.coerce.number(),
  total_executions: z.number(),
  total_prompt_tokens: z.number(),
  total_completion_tokens: z.number(),
  by_agent: z.array(agentCostEntrySchema),
  by_project: z.array(projectCostEntrySchema),
  period_from: z.string().nullable(),
  period_to: z.string().nullable(),
})

export type OrganizationCostSummary = z.infer<typeof organizationCostSummarySchema>

/* -------------------------------------------------------------------------- */
/* Métricas do atendimento                                                     */
/* -------------------------------------------------------------------------- */

/**
 * `valor` NULO É SEM DADO NENHUM no período — o sinal de consumidor parado.
 * Zero é "o sistema estava vivo e isto não aconteceu". Os dois são diferentes,
 * e a tela precisa desenhá-los diferente.
 */
export const pontoSchema = z.object({
  instante: z.string(),
  valor: z.number().nullable(),
})

export type Ponto = z.infer<typeof pontoSchema>

export const conversaSchema = z.object({
  session_id: z.string().uuid(),
  turnos: z.number(),
  tokens_in: z.number(),
  tokens_out: z.number(),
  cost: z.number(),
})

export type Conversa = z.infer<typeof conversaSchema>

export const fatiaSchema = z.object({
  categoria: z.string(),
  quantidade: z.number(),
})

export type Fatia = z.infer<typeof fatiaSchema>

export const conversaoSchema = z.object({
  objetivo: z.string(),
  abertos: z.number(),
  convertidos: z.number(),
  taxa: z.number().nullable(),
  fora_do_funil: z.number().nullable().optional(),
})

export type Conversao = z.infer<typeof conversaoSchema>

/* -------------------------------------------------------------------------- */
/* O funil em tela (spec 117)                                                  */
/* -------------------------------------------------------------------------- */

export const degrauSchema = z.object({
  indice: z.number().int(),
  /** Como o escritório chama a etapa. Nulo quando o documento não a declara mais. */
  nome: z.string().nullable(),
  alcancaram: z.number().int(),
  queda_ate_aqui: z.number().int(),
})
export type Degrau = z.infer<typeof degrauSchema>

export const cascataSchema = z.object({
  objetivo: z.string().nullable(),
  etapas: z.array(degrauSchema),
  aviso_do_denominador: z.string(),
  // OS CANAIS VÊM DO ESCRITÓRIO, como os nomes das etapas: o filtro os
  // oferecia escritos no código (`whatsapp`, `telegram`), o que é vocabulário
  // de canal dentro do produto e não serve a quem atende por outro.
  canais: z.array(z.string()).default([]),
})
export type Cascata = z.infer<typeof cascataSchema>

/**
 * Se a seção se aplica a este escritório — e, quando não, por quê.
 *
 * São DOIS vazios, não um: "ainda não aconteceu" e "não se aplica". Sem busca
 * no acervo, dizer "nenhuma pergunta ficou sem resposta" afirma um acerto que
 * ninguém observou.
 */
export const aplicabilidadeSchema = z.object({
  aplicavel: z.boolean(),
  por_que_nao_se_aplica: z.string().nullable(),
})
export type Aplicabilidade = z.infer<typeof aplicabilidadeSchema>

export const motivoSchema = z.object({
  motivo: z.string(),
  fonte: z.string(),
  quantidade: z.number().int(),
})
export type Motivo = z.infer<typeof motivoSchema>

export const perguntaSchema = z.object({
  pergunta: z.string(),
  quantas: z.number().int(),
  /**
   * O ENDEREÇO DA PERGUNTA — o hash pelo qual a listagem a recorta.
   *
   * Pelo hash e não pelo texto: o texto exibido é `min()` do grupo, e duas
   * normalizações do mesmo hash levariam a recortes diferentes.
   */
  chave: z.string().default(''),
})
export type Pergunta = z.infer<typeof perguntaSchema>

/* -------------------------------------------------------------------------- */
/* O agente que atende, e o documento dele                                     */
/* -------------------------------------------------------------------------- */

export const agenteDeAtendimentoSchema = z.object({
  agent_id: z.string().uuid().nullable(),
  agent_name: z.string().nullable(),
  blueprint_version: z.number().nullable(),
  blueprint_published_at: z.string().nullable(),
})

export type AgenteDeAtendimento = z.infer<typeof agenteDeAtendimentoSchema>

/* As caixas de entrada da conta do canal e o agente de cada uma (spec 157). */
export const caixaDaContaSchema = z.object({
  inbox_id: z.number(),
  name: z.string(),
  channel_type: z.string().nullable().optional(),
  link_id: z.string().uuid().nullable(),
  agent_id: z.string().uuid().nullable(),
  agent_name: z.string().nullable(),
})
export type CaixaDaConta = z.infer<typeof caixaDaContaSchema>

export const vinculoQualquerCaixaSchema = z.object({
  link_id: z.string().uuid(),
  agent_id: z.string().uuid(),
  agent_name: z.string().nullable(),
})

export const caixasResponseSchema = z.object({
  chatwoot_account_id: z.number(),
  inboxes: z.array(caixaDaContaSchema),
  any_inbox: vinculoQualquerCaixaSchema.nullable(),
})
export type CaixasDaConta = z.infer<typeof caixasResponseSchema>

export const vinculoDeCaixaSchema = z.object({
  link_id: z.string().uuid(),
  inbox_id: z.number().nullable(),
  agent_id: z.string().uuid(),
  agent_name: z.string().nullable(),
  is_active: z.boolean(),
})
export type VinculoDeCaixa = z.infer<typeof vinculoDeCaixaSchema>

export const timeDoCanalSchema = z.object({
  id: z.number(),
  name: z.string(),
  members_count: z.number(),
})
export type TimeDoCanal = z.infer<typeof timeDoCanalSchema>

export const blueprintVersaoSchema = z.object({
  version: z.number(),
  status: z.string(),
  /** `legacy` (documento único), `organization`, `project` ou `agent` (spec 157). */
  scope: z.string().optional(),
  published_at: z.string().nullable(),
  /** Na publicação da organização: a conferência de cada atendimento (FR-004). */
  checked: z
    .array(
      z.object({
        agent_id: z.string(),
        agent_name: z.string().nullable(),
        ok: z.boolean(),
      })
    )
    .nullable()
    .optional(),
  warnings: z.array(z.string()).nullable().optional(),
})

/** Um atendimento da organização: agente, projeto, caixas e versão no ar (R-11). */
export const atendimentoSchema = z.object({
  agent_id: z.string().uuid(),
  name: z.string(),
  project_id: z.string().uuid(),
  project_name: z.string(),
  inboxes: z.array(
    z.object({ chatwoot_account_id: z.number(), inbox_id: z.number().nullable() })
  ),
  published_version: z.number().nullable(),
  is_active: z.boolean(),
})
export type Atendimento = z.infer<typeof atendimentoSchema>

/** A recusa da publicação da organização: cada atendimento e campo (FR-004). */
export const recusaDaOrganizacaoSchema = z.object({
  code: z.literal('org_config_breaks_agents'),
  agents: z.array(
    z.object({
      agent_id: z.string(),
      agent_name: z.string().nullable(),
      errors: z.array(z.object({ loc: z.array(z.union([z.string(), z.number()])), msg: z.string() })),
    })
  ),
})
export type RecusaDaOrganizacao = z.infer<typeof recusaDaOrganizacaoSchema>

export type BlueprintVersao = z.infer<typeof blueprintVersaoSchema>

export const validacaoSchema = z.object({
  valido: z.boolean(),
  detail: z.array(z.record(z.unknown())).default([]),
})

export type Validacao = z.infer<typeof validacaoSchema>

export const cenarioSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  blueprint_version: z.number().nullable(),
  transcript: z.record(z.unknown()),
  assertions: z.array(z.record(z.unknown())),
})

export type Cenario = z.infer<typeof cenarioSchema>

/** Quem pode mudar o campo, lido do JSON Schema publicado. */
export type EditavelPor = 'tenant' | 'operador'
